import React, { useMemo } from 'react';
import Workspace from '../../components/Workspace/Workspace';
import { SalesInvoiceLine } from '../../graphql/entities';
import Formatters from '../../components/grid-addons/formatters';
import getProductDP from '../../utils/getProductDP';
import { WorkspaceActionButtons } from '../../components/Workspace/WorkspaceActionButtons';
import { useGenerateCreditFromInvoiceLinesAction } from '../../actions/salesLedger/SalesInvoiceLineWorkspaceAction';
import { useWorkspace } from '../../graphql/hooks/useWorkspace';
import { Direction, FilterOperation, ISalesInvoiceLine, ProductClass } from '../../graphql/schema/p4types';
import { Paths } from '../../@types/keyOfNested';
import { useAttributesForWorkspace } from '../../components/Workspace/useAttributesForWorkspace';
import productAttributeQuery from '../../graphql/queries/product/ProductAttributeConfigListQuery.graphql';
import customerAttributeQuery from '../../graphql/queries/customer/CustomerAttributeConfigListQuery.graphql';
import { IWorkspaceColumnOverride } from '../../@types/workspaceTypes';
import SalesOrderFormModalFormatter from '../../components/modals/formatters/SalesOrderFormModalFormatter';
import SalesInvoiceFormModalFormatter from '../../components/modals/formatters/SalesInvoiceFormModalFormatter';
import { useAbstractSalesLineColumnOverrides } from '../../utils/abstractSalesHooks';
import salesAttributeQuery from '../../graphql/queries/sales/SalesAttributeConfigListQuery.graphql';
import { usePrecision } from '../../utils/hooks';
import AliasFormatter from '../../components/grid-addons/formatters/AliasFormatter';

const { formUrl, type, title, workspace: workspaceQuery } = SalesInvoiceLine;

const SalesInvoiceLineWorkspaceActions = (props) => {
  const generateCreditFromLinesActions = useGenerateCreditFromInvoiceLinesAction();

  //View Context wrapper is to 'hide' the workspace view context from the form. Otherwise we get some unintentional side effects (e.g. Shimmer rows on the item grid).
  return <WorkspaceActionButtons {...props} actionButtons={generateCreditFromLinesActions} />;
};

const SALES_INVOICE_LINE_FIELD_NAMES: Array<Paths<ISalesInvoiceLine>> = [
  'salesInvoice.documentNumber',
  'salesOrderLine.salesOrder.orderNumber',
  'createdAt',
  'product.reference',
  'product.description',
  'nominal.name',
  'grossSellingPrice',
  'quantity',
  'activeSellingUnit.description',
  'nettLineValue',
];

const getPricePrecisionColumnOverrides = (
  costPricePrecision: number,
  sellingPricePrecision: number
): IWorkspaceColumnOverride => {
  return {
    grossSellingPrice: {
      name: 'Selling Price',
      options: { format: { decimals: sellingPricePrecision } },
    },
    singleUnitGrossSellingPrice: {
      name: 'Single Unit Selling Price',
      options: { format: { decimals: sellingPricePrecision } },
    },
    standardUnitGrossSellingPrice: {
      name: 'Standard Unit Selling Price',
      options: { format: { decimals: sellingPricePrecision } },
    },
    nettSellingPrice: {
      name: 'Nett Selling Price',
      options: { format: { decimals: sellingPricePrecision } },
    },
    singleUnitNettSellingPrice: {
      name: 'Single Unit Nett Selling Price',
      options: { format: { decimals: sellingPricePrecision } },
    },
    'potentialPrices.grossPrice': {
      name: 'Potential Prices - Gross Price',
      options: { format: { decimals: sellingPricePrecision } },
    },
    'potentialPrices.nettPrice': {
      name: 'Potential Prices - Nett Price',
      options: { format: { decimals: sellingPricePrecision } },
    },
    profitValue: {
      name: 'Profit Value',
      options: { format: { decimals: sellingPricePrecision } },
    },
    singleUnitProfitValue: {
      name: 'Single Unit Profit Value',
      options: { format: { decimals: sellingPricePrecision } },
    },
    haggleProfitValue: {
      name: 'Haggle Profit Value',
      options: { format: { decimals: sellingPricePrecision } },
    },
    singleUnitHaggleProfitValue: {
      name: 'Single Unit Haggle Profit Value',
      options: { format: { decimals: sellingPricePrecision } },
    },
    costPrice: {
      name: 'Cost Price',
      options: { format: { decimals: costPricePrecision } },
    },
    singleUnitCostPrice: {
      name: 'Single Unit Cost Price',
      options: { format: { decimals: costPricePrecision } },
    },
    haggleCostPrice: {
      name: 'Haggle Cost',
      options: { format: { decimals: costPricePrecision } },
    },
    singleUnitHaggleCostPrice: {
      name: 'Single Unit Haggle Cost',
      options: { format: { decimals: costPricePrecision } },
    },
  };
};

const SalesInvoiceLineWorkspace = (props) => {
  const costPricePrecision = usePrecision('cost');
  const sellingPricePrecision = usePrecision('sales');
  const columnOverrides: IWorkspaceColumnOverride = useMemo(
    () => ({
      'salesInvoice.documentNumber': {
        name: 'Number',
        key: 'salesInvoice.documentNumber',
        width: 100,
        formatter: SalesInvoiceFormModalFormatter,
        options: {
          entityId: 'salesInvoice.id',
        },
      },
      'salesOrderLine.salesOrder.orderNumber': {
        name: 'Order Number',
        key: 'salesOrderLine.salesOrder.orderNumber',
        width: 100,
        formatter: SalesOrderFormModalFormatter,
        options: {
          entityId: 'salesOrderLine.salesOrder.id',
        },
      },
      createdAt: {
        name: 'Created At',
        width: 150,
      },
      quantity: {
        formatter: Formatters.NumericFormatter,
        cellClass: 'rightAlign',
        options: {
          format: { decimals: getProductDP },
        },
      },
      'product.mainSupplierDetails.supplier.reference': {
        name: 'Main Supplier - Reference',
        sortable: false,
      },
      'product.mainSupplierDetails.supplier.name': {
        name: 'Main Supplier - Name',
        sortable: false,
      },
      'product.weight': {
        name: 'Product - Unit Weight',
      },
      'amazonSalesInvoiceLineEmbeddable.amazonCarriage': {
        name: 'Amazon Carriage',
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: 2 },
        },
      },
      'amazonSalesInvoiceLineEmbeddable.amazonCommission': {
        name: 'Amazon Commission',
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: 2 },
        },
      },
      'amazonSalesInvoiceLineEmbeddable.amazonFbaFees': {
        name: 'Amazon FBA Fees',
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: 2 },
        },
      },
      'amazonSalesInvoiceLineEmbeddable.amazonProfitMargin': {
        name: 'Amazon Profit Margin',
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: 2 },
        },
      },
      'amazonSalesInvoiceLineEmbeddable.amazonProfitValue': {
        name: 'Amazon Profit Value',
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: 2 },
        },
      },
      'amazonSalesInvoiceLineEmbeddable.amazonTotalFees': {
        name: 'Amazon Total Fees',
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: 2 },
        },
      },
      costPrice: {
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: costPricePrecision },
        },
      },
      baseLineCost: {
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: costPricePrecision },
        },
      },
      haggleCostPrice: {
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: costPricePrecision },
        },
      },
      haggleLineCost: {
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: costPricePrecision },
        },
      },
      lineCost: {
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: costPricePrecision },
        },
      },
      singleUnitCostPrice: {
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: costPricePrecision },
        },
      },
      singleUnitHaggleCostPrice: {
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: costPricePrecision },
        },
      },
      nettSellingPrice: {
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: sellingPricePrecision },
        },
      },
      nettLineValue: {
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: sellingPricePrecision },
        },
      },
      singleUnitGrossSellingPrice: {
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: sellingPricePrecision },
        },
      },
      singleUnitNettSellingPrice: {
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: sellingPricePrecision },
        },
      },
      grossSellingPrice: {
        formatter: Formatters.NumericFormatter,
        options: {
          format: { decimals: sellingPricePrecision },
        },
      },
      'salesInvoice.deliveryAddress.country.isoCountry.name': {
        name: 'Delivery Address - Country Name',
      },
      'salesInvoice.deliveryAddress.country.isoCountry.codeAlpha2': {
        name: 'Delivery Address - Code Alpha 2',
      },
      'salesInvoice.deliveryAddress.country.isoCountry.codeAlpha3': {
        name: 'Delivery Address - Code Alpha 3',
      },
      'salesInvoice.deliveryAddress.country.description': {
        name: 'Delivery Address - Country',
      },
      'salesInvoice.invoiceAddress.country.isoCountry.name': {
        name: 'Invoice Address - Country Name',
      },
      'salesInvoice.invoiceAddress.country.isoCountry.codeAlpha2': {
        name: 'Invoice Address - Code Alpha 2',
      },
      'salesInvoice.invoiceAddress.country.isoCountry.codeAlpha3': {
        name: 'Invoice Address - Code Alpha 3',
      },
      'salesInvoice.invoiceAddress.country.description': {
        name: 'Invoice Address - Country',
      },
      'product.alias.reference': {
        name: 'Product Alias References',
        formatter: AliasFormatter,
      },
      'salesInvoice.currency.isoCode.codeAlpha3': {
        name: 'Sales Invoice Currency - Code Alpha 3',
      },
      'salesInvoice.currency.isoCode.name': {
        name: 'Sales Invoice Currency - Name',
      },
      lineOrder: {
        name: 'Line Number',
      },
      ...getPricePrecisionColumnOverrides(costPricePrecision, sellingPricePrecision),
    }),
    [costPricePrecision, sellingPricePrecision]
  );

  const salesInvoiceLineColumnOverrides = useAbstractSalesLineColumnOverrides(
    columnOverrides,
    costPricePrecision,
    sellingPricePrecision
  );

  const productAttributeColumns = useAttributesForWorkspace(productAttributeQuery, {
    valuesPath: 'product.attributeValues',
    columnKey: 'product.attributeValues.attributeConfig.title',
  });

  const customerAttributeColumns = useAttributesForWorkspace(customerAttributeQuery, {
    valuesPath: 'salesInvoice.customer.attributeValues',
    columnKey: 'salesInvoice.customer.attributeValues.attributeConfig.title',
  });

  const salesAttributeColumns = useAttributesForWorkspace(salesAttributeQuery, {
    columnKey: 'salesInvoice.attributeValues.attributeConfig.title',
    valuesPath: 'salesInvoice.attributeValues',
  });

  const customColumns = useMemo(
    () => ({ ...productAttributeColumns, ...customerAttributeColumns, ...salesAttributeColumns }),
    [productAttributeColumns, customerAttributeColumns, salesAttributeColumns]
  );

  const workspaceProps = useWorkspace<ISalesInvoiceLine>(props, SalesInvoiceLine, {
    columns: SALES_INVOICE_LINE_FIELD_NAMES,
    columnOverrides: salesInvoiceLineColumnOverrides,
    customColumns: customColumns,
    initialSortColumn: 'salesInvoice.documentNumber',
    initialSortDirection: Direction.Desc,
    filters: [
      {
        filterOperation: FilterOperation.NotEquals,
        filterParameters: {
          field: 'product.productClass',
          value: ProductClass.FreeType,
        },
      },
    ],
  });

  return (
    <Workspace
      entityType={type}
      title={title}
      formUrl={formUrl}
      defaultQuery={workspaceQuery}
      {...workspaceProps}
      {...props}
      entity={SalesInvoiceLine}
      formIdOverride={`salesInvoice.id`}
      workspaceActionButtonsHOC={SalesInvoiceLineWorkspaceActions}
    />
  );
};

export default SalesInvoiceLineWorkspace;
